import { CalendarDateRangeItem, CalendarRefType, useDialog } from "common";
import { VIEWS } from "common/components/CalendarPro";
import { BookingItem } from "features/bookings/bookingApi";
import {
  CalendarEventItem,
  EventItem,
  getEventCalendarService,
  prepareCalendarItems,
} from "features/events/eventApi";
import useSettings from "features/settings/useSettings";
import { useWebSocket } from "features/websoket/WebSocketProvider";
import usePersistentState from "helpers/usePersistentState";
import moment, { Moment } from "moment";
import QueryString from "qs";
import { useEffect, useRef, useState } from "react";
import { setCalendarLoading } from "./CalendarToolbar";

export function calendarAction(action: "refresh" | "setItem", item?: any) {
  let event = new CustomEvent("calendarAction", {
    detail: {
      action,
      item,
    },
  });
  document.dispatchEvent(event);
}
export interface UseEventCalendarProps {
  useEvents?: boolean;
  boatIds?: any[];
  programIds?: any[];
  defaultItems?: any[];
  date?: Moment;
  filterKey: string;
  filterStorageType?: "none" | "local" | "session";
}
export default function useEventCalendar({
  useEvents = true,
  boatIds: _boatIds,
  programIds: _programIds,
  date,
  defaultItems,
  filterKey,
  filterStorageType = "none",
}: UseEventCalendarProps) {
  const calService = getEventCalendarService({});
  const cal = useRef<CalendarRefType<CalendarEventItem> | undefined>();
  const [settings, setSettings] = useSettings({ key: "calendar" });
  const _storageDate = sessionStorage.getItem(`calendar.${filterKey}.date`);
  const initialDate = useRef<Moment | undefined>(
    _storageDate ? moment(_storageDate) : undefined
  );
  const initialView = useRef<VIEWS>(
    (sessionStorage.getItem(`calendar.${filterKey}.view`) as any) ||
      settings.view ||
      VIEWS.DAY
  );
  const { subscribe, unsubscribe } = useWebSocket();

  const [boatIds, setBoatIds] = usePersistentState<any[] | undefined>(
    _boatIds,
    `calendar.${filterKey}.boatIds`,
    filterStorageType
  );
  const [programIds, setProgramIds] = usePersistentState<any[] | undefined>(
    _programIds,
    `calendar.${filterKey}.programIds`,
    filterStorageType
  );
  const [items, setItems] = useState<Array<CalendarEventItem>>(
    defaultItems || []
  );
  const init = useRef(false);
  useEffect(() => {
    if (init.current) {
      if (JSON.stringify(programIds) !== JSON.stringify(_programIds)) {
        setProgramIds(_programIds);
      }
    } else {
      init.current = true;
    }
  }, [_programIds]);

  const [event, setEvent, eventDialog] = useDialog<Partial<EventItem>>();
  const [booking, setBooking, bookingDialog] =
    useDialog<Partial<BookingItem>>();

  async function loadEvents(range: CalendarDateRangeItem, silent?: boolean) {
    if (cal.current?.date) {
      sessionStorage.setItem(
        `calendar.${filterKey}.date`,
        cal.current?.date?.format("YYYY-MM-DD")
      );
    } else {
      sessionStorage.removeItem(`calendar.${filterKey}.date`);
    }
    if (cal.current?.view) {
      sessionStorage.setItem(`calendar.${filterKey}.view`, cal.current?.view);
    } else {
      sessionStorage.removeItem(`calendar.${filterKey}.view`);
    }
    if (!silent) {
      setCalendarLoading(true);
    }
    let params: any = {
      from: range.start.format("YYYY-MM-DD"),
      to: range.end.format("YYYY-MM-DD"),
    };
    if (boatIds) {
      params.boatIds = boatIds;
    }
    if (programIds) {
      params.programIds = programIds;
    }
    const queryParams = QueryString.stringify(params);
    const { success, data } = await calService.paginateItems(queryParams);
    if (!silent) {
      setCalendarLoading(false);
    }
    if (success) {
      subscribe("calendar", params.from, params.to, dates => {
        cal.current?.refresh(true);
      });
      return setItems(prepareCalendarItems(data || []));
    }
    return [];
  }
  const shouldRefresh = useRef(false);
  useEffect(() => {
    if (shouldRefresh.current) {
      cal.current?.refresh();
    } else {
      shouldRefresh.current = true;
    }
  }, [boatIds, programIds]);

  function handleCalendar({
    detail: { action, item },
  }: {
    detail: { action: "refresh" | "setItem"; item?: any };
  }) {
    if (action === "refresh") {
      cal.current?.refresh();
    }
    if (action === "setItem") {
      console.log({ item });
      let newItems = items.map(i => {
        return i.id === item.id ? prepareCalendarItems([item])[0] : i;
      });
      setItems(newItems);
    }
  }
  useEffect(() => {
    if (useEvents) {
      //@ts-ignore
      document.addEventListener("calendarAction", handleCalendar);
      return () => {
        unsubscribe("calendar");
        //@ts-ignore
        document.removeEventListener("calendarAction", handleCalendar);
      };
    }
    return () => unsubscribe("calendar");
  }, []);

  return {
    boatIds,
    setBoatIds,
    items,
    event,
    setEvent,
    eventDialog,
    booking,
    setBooking,
    bookingDialog,
    loadEvents,
    cal,
    programIds,
    setProgramIds,
    setItems,
    settings,
    setSettings,
    initialDate,
    initialView,
  };
}
